import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Calendar, Clock, AlertTriangle, CheckCircle, 
  Phone, Mail, User, MapPin, RefreshCw, Building2, ChevronRight,
  Ban, Star, ThumbsUp, Filter, CalendarDays
} from 'lucide-react';
import { Button } from '../components/ui/button';
import BusinessStatusModal from '../components/BusinessStatusModal';
import FollowUpCalendar from '../components/FollowUpCalendar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  new: { label: 'Neu', color: 'bg-gray-100 text-gray-700', icon: Building2 },
  visited: { label: 'Besucht', color: 'bg-blue-100 text-blue-700', icon: Clock },
  interested: { label: 'Interessiert', color: 'bg-green-100 text-green-700', icon: ThumbsUp },
  follow_up: { label: 'Follow-up', color: 'bg-yellow-100 text-yellow-700', icon: Calendar },
  not_interested: { label: 'Kein Interesse', color: 'bg-red-100 text-red-700', icon: Ban },
  partner: { label: 'Partner', color: 'bg-brand-green/20 text-brand-green', icon: Star }
};

const VertriebContactsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('salesperson_token');
  
  const [activeTab, setActiveTab] = useState('follow_ups');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [followUps, setFollowUps] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [blacklist, setBlacklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal state
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/vertrieb/login');
      return;
    }
    loadData();
  }, [token, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [followUpsRes, statsRes, contactsRes, blacklistRes] = await Promise.all([
        fetch(`${API_URL}/api/business-contacts/follow-ups`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/business-contacts/stats/summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/business-contacts/my`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/business-contacts/blacklist`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (followUpsRes.ok) setFollowUps(await followUpsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (contactsRes.ok) {
        const data = await contactsRes.json();
        setContacts(data.contacts || []);
      }
      if (blacklistRes.ok) {
        const data = await blacklistRes.json();
        setBlacklist(data.blacklist || []);
      }
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromBlacklist = async (placeId) => {
    try {
      const response = await fetch(`${API_URL}/api/business-contacts/${placeId}/blacklist`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        loadData();
      }
    } catch (error) {
      console.error('Error removing from blacklist:', error);
    }
  };

  const handleStatusUpdated = () => {
    loadData();
    setShowModal(false);
    setSelectedBusiness(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const ContactCard = ({ contact, showActions = true }) => {
    const statusConfig = STATUS_CONFIG[contact.status] || STATUS_CONFIG.new;
    const StatusIcon = statusConfig.icon;
    
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                <StatusIcon className="w-3 h-3 inline mr-1" />
                {statusConfig.label}
              </span>
              {contact.rating && (
                <span className="text-xs text-gray-500">
                  ⭐ {contact.rating}
                </span>
              )}
            </div>
            <h3 className="font-semibold text-gray-900 truncate">{contact.name}</h3>
            <p className="text-sm text-gray-500 truncate flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {contact.address}
            </p>
            
            {/* Contact Info */}
            {contact.contact_person && (
              <p className="text-sm text-gray-600 mt-2 flex items-center gap-1">
                <User className="w-3 h-3" />
                {contact.contact_person}
              </p>
            )}
            {contact.contact_phone && (
              <a href={`tel:${contact.contact_phone}`} className="text-sm text-brand-green flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {contact.contact_phone}
              </a>
            )}
            
            {/* Follow-up Info */}
            {contact.status === 'follow_up' && contact.follow_up_date && (
              <div className="mt-2 bg-yellow-50 rounded-lg p-2">
                <p className="text-xs font-medium text-yellow-700 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Termin: {formatDate(contact.follow_up_date)}
                </p>
                {contact.follow_up_notes && (
                  <p className="text-xs text-yellow-600 mt-1">{contact.follow_up_notes}</p>
                )}
              </div>
            )}
          </div>
          
          {showActions && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setSelectedBusiness(contact);
                setShowModal(true);
              }}
              className="flex-shrink-0"
            >
              Status ändern
            </Button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-brand-green" />
      </div>
    );
  }

  const filteredContacts = statusFilter === 'all' 
    ? contacts 
    : contacts.filter(c => c.status === statusFilter);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/vertrieb/dashboard')}
              className="text-gray-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-brand-green" />
              Meine Kontakte
            </h1>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={loadData}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        
        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {Object.entries(stats.stats).map(([status, count]) => {
              const config = STATUS_CONFIG[status];
              if (!config) return null;
              const Icon = config.icon;
              return (
                <div 
                  key={status}
                  className={`rounded-xl p-3 ${config.color} cursor-pointer hover:opacity-80 transition-opacity`}
                  onClick={() => {
                    setActiveTab('contacts');
                    setStatusFilter(status);
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    <span className="text-2xl font-bold">{count}</span>
                  </div>
                  <p className="text-xs mt-1">{config.label}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-gray-200 pb-2 overflow-x-auto">
          <button
            onClick={() => setActiveTab('follow_ups')}
            className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
              activeTab === 'follow_ups' 
                ? 'bg-yellow-500 text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="tab-follow-ups"
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Follow-ups ({followUps?.total || 0})
          </button>
          <button
            onClick={() => { setActiveTab('contacts'); setStatusFilter('all'); }}
            className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
              activeTab === 'contacts' 
                ? 'bg-brand-green text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="tab-contacts"
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            Alle Kontakte ({contacts.length})
          </button>
          <button
            onClick={() => setActiveTab('blacklist')}
            className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
              activeTab === 'blacklist' 
                ? 'bg-red-500 text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="tab-blacklist"
          >
            <Ban className="w-4 h-4 inline mr-2" />
            Blacklist ({blacklist.length})
          </button>
          
          {/* View Mode Toggle - nur für Follow-ups */}
          {activeTab === 'follow_ups' && (
            <div className="ml-auto flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'list' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="view-list"
              >
                Liste
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'calendar' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="view-calendar"
              >
                <CalendarDays className="w-4 h-4 inline mr-1" />
                Kalender
              </button>
            </div>
          )}
        </div>

        {/* Follow-ups Tab */}
        {activeTab === 'follow_ups' && followUps && viewMode === 'list' && (
          <div className="space-y-6">
            {/* Overdue */}
            {followUps.overdue?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-red-600 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Überfällig ({followUps.overdue.length})
                </h3>
                <div className="space-y-3">
                  {followUps.overdue.map((contact) => (
                    <ContactCard key={contact.place_id} contact={contact} />
                  ))}
                </div>
              </div>
            )}

            {/* Today */}
            {followUps.today?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-orange-600 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Heute ({followUps.today.length})
                </h3>
                <div className="space-y-3">
                  {followUps.today.map((contact) => (
                    <ContactCard key={contact.place_id} contact={contact} />
                  ))}
                </div>
              </div>
            )}

            {/* This Week */}
            {followUps.this_week?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-yellow-600 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Diese Woche ({followUps.this_week.length})
                </h3>
                <div className="space-y-3">
                  {followUps.this_week.map((contact) => (
                    <ContactCard key={contact.place_id} contact={contact} />
                  ))}
                </div>
              </div>
            )}

            {/* Later */}
            {followUps.later?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Später ({followUps.later.length})
                </h3>
                <div className="space-y-3">
                  {followUps.later.map((contact) => (
                    <ContactCard key={contact.place_id} contact={contact} />
                  ))}
                </div>
              </div>
            )}

            {/* No Date */}
            {followUps.no_date?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-400 mb-3">
                  Ohne Datum ({followUps.no_date.length})
                </h3>
                <div className="space-y-3">
                  {followUps.no_date.map((contact) => (
                    <ContactCard key={contact.place_id} contact={contact} />
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {followUps.total === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">Keine Follow-ups</p>
                <p className="text-sm">Alle Termine wurden abgearbeitet!</p>
              </div>
            )}
          </div>
        )}

        {/* Follow-ups Calendar View */}
        {activeTab === 'follow_ups' && followUps && viewMode === 'calendar' && (
          <div className="space-y-4">
            <FollowUpCalendar 
              followUps={[
                ...(followUps.overdue || []),
                ...(followUps.today || []),
                ...(followUps.this_week || []),
                ...(followUps.later || [])
              ]}
              onSelectFollowUp={(fu) => {
                setSelectedBusiness(fu);
                setShowModal(true);
              }}
            />
            
            {/* Quick Legend */}
            <div className="flex items-center gap-4 text-xs text-gray-500 justify-center">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-red-500"></span> Überfällig
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-yellow-500"></span> Geplant
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-brand-green"></span> Heute
              </span>
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="space-y-4">
            {/* Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">Alle Status</option>
                {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                  <option key={value} value={value}>{config.label}</option>
                ))}
              </select>
              <span className="text-sm text-gray-500">
                {filteredContacts.length} Betriebe
              </span>
            </div>

            {/* Contact List */}
            <div className="space-y-3">
              {filteredContacts.map((contact) => (
                <ContactCard key={contact.place_id} contact={contact} />
              ))}
            </div>

            {/* Empty State */}
            {filteredContacts.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">Keine Kontakte</p>
                <p className="text-sm">Erstelle eine Tour um Betriebe zu entdecken</p>
              </div>
            )}
          </div>
        )}

        {/* Blacklist Tab */}
        {activeTab === 'blacklist' && (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm text-red-700">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                Diese Betriebe werden bei neuen Touren automatisch ausgeblendet.
              </p>
            </div>

            <div className="space-y-3">
              {blacklist.map((contact) => (
                <div key={contact.place_id} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{contact.name}</h3>
                      <p className="text-sm text-gray-500 truncate flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {contact.address}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Blockiert am: {formatDate(contact.status_updated_at)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRemoveFromBlacklist(contact.place_id)}
                      className="text-green-600 border-green-300 hover:bg-green-50"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Reaktivieren
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Empty State */}
            {blacklist.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Ban className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">Blacklist ist leer</p>
                <p className="text-sm">Keine Betriebe wurden blockiert</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Status Modal */}
      <BusinessStatusModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedBusiness(null);
        }}
        business={selectedBusiness}
        token={token}
        onStatusUpdated={handleStatusUpdated}
      />
    </div>
  );
};

export default VertriebContactsPage;
